/**
 * RestaurantDetailPage Component
 * Phase D4.5.2: Voice-driven restaurant booking
 *
 * Elegant restaurant booking flow with voice recognition.
 * Transferred from AskZaxiaView - handles time, party size, and confirmation.
 * Voice input is handled locally (spacebar for push-to-talk).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { ArrowLeft, Check, Mic, Calendar, Clock, Users } from 'lucide-react';
import { zaxiaVoice, createSpeechRecognition, isSpeechRecognitionAvailable, type SpeechRecognitionEvent } from '@/services';
import { processVoiceInput, createVoiceContext, type VoiceAction } from '@/voice';
import type { CreateBookingRequest } from '@/api';

// =============================================================================
// TYPES
// =============================================================================

export interface VoiceHintConfig {
  title: string;
  hints: string[];
  variant?: 'default' | 'action' | 'confirm';
}

export interface RestaurantBookingSlots {
  time?: string;      // HH:MM format
  partySize?: number;
  transcript?: string; // Original transcript for context
}

interface RestaurantDetailPageProps {
  isActive: boolean;
  onBack: () => void;
  onBookingComplete: (booking: CreateBookingRequest) => Promise<void>;
  onVoiceHintsChange?: (hints: VoiceHintConfig | null) => void;
  /** Pre-filled booking slots from voice command */
  initialSlots?: RestaurantBookingSlots;
  /** Stay dates for date selection */
  stayCheckIn?: string;
  stayCheckOut?: string;
}

type BookingStep = 'select-date' | 'select-time' | 'select-party-size' | 'review' | 'confirm';

interface RestaurantBooking {
  time: string | null;      // HH:MM
  partySize: number | null;
  date: string | null;      // YYYY-MM-DD
}

// =============================================================================
// CONSTANTS
// =============================================================================

const RESTAURANT_IMAGES = [
  'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&h=600&fit=crop',
];

const RESTAURANT_INFO = {
  name: 'Terra Restaurant',
  cuisine: 'Mediterranean Fine Dining',
  description: 'Our award-winning restaurant offers Mediterranean cuisine with stunning views. Chef Miguel crafts seasonal menus using locally sourced ingredients.',
  location: 'Ground Floor, Main Building',
  operatingHours: 'Breakfast 7:00-10:30 • Lunch 12:00-15:00 • Dinner 19:00-22:30',
  dressCode: 'Smart casual for breakfast/lunch. Smart elegant for dinner.',
};

// Available time slots for dinner
const DINNER_TIME_SLOTS = ['19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'];
const LUNCH_TIME_SLOTS = ['12:00', '12:30', '13:00', '13:30', '14:00', '14:30'];

// Party size options
const PARTY_SIZES = [1, 2, 3, 4, 5, 6];

// =============================================================================
// TIME/PARTY SIZE PARSING (transferred from AskZaxiaView)
// =============================================================================

const TIME_PATTERNS = [
  // "8:30pm", "8:30 pm"
  { pattern: /\b(\d{1,2}):(\d{2})\s*(pm|p\.?m\.?)\b/i, parse: (m: RegExpMatchArray) => {
    const h = parseInt(m[1]!);
    return `${h < 12 ? h + 12 : h}:${m[2]}`;
  }},
  // "8:30am", "8:30 am"
  { pattern: /\b(\d{1,2}):(\d{2})\s*(am|a\.?m\.?)\b/i, parse: (m: RegExpMatchArray) => `${m[1]!.padStart(2, '0')}:${m[2]}` },
  // "8pm", "8 pm", "8 p.m." - without colon
  { pattern: /\b(\d{1,2})\s*(pm|p\.?m\.?)\b/i, parse: (m: RegExpMatchArray) => {
    const h = parseInt(m[1]!);
    return `${h < 12 ? h + 12 : h}:00`;
  }},
  // "8am", "8 am"
  { pattern: /\b(\d{1,2})\s*(am|a\.?m\.?)\b/i, parse: (m: RegExpMatchArray) => `${m[1]!.padStart(2, '0')}:00` },
  // "19:00", "20:30" - 24h time
  { pattern: /\b(\d{1,2}):(\d{2})\b/i, parse: (m: RegExpMatchArray) => `${m[1]!.padStart(2, '0')}:${m[2]}` },
  // Words - evening times
  { pattern: /\b(seven|7)\s*(o.?clock)?(\s*(in the\s*)?(evening|tonight|pm))?\b/i, parse: () => '19:00' },
  { pattern: /\b(eight|8)\s*(o.?clock)?(\s*(in the\s*)?(evening|tonight|pm))?\b/i, parse: () => '20:00' },
  { pattern: /\b(nine|9)\s*(o.?clock)?(\s*(in the\s*)?(evening|tonight|pm))?\b/i, parse: () => '21:00' },
  { pattern: /\bhalf\s*(past)?\s*(seven|7)\b/i, parse: () => '19:30' },
  { pattern: /\bhalf\s*(past)?\s*(eight|8)\b/i, parse: () => '20:30' },
  { pattern: /\btonight\b/i, parse: () => '20:00' }, // Default tonight to 8pm
];

const PARTY_SIZE_PATTERNS = [
  { pattern: /\bjust\s*(me|myself)\b|for\s*one\b|\b(one|1)\s*(person|guest|people)?\b/i, size: 1 },
  { pattern: /\b(two|2)\s*(people|persons|guests)?\b|couple|just\s*(us|the two of us)\b|for\s*two\b/i, size: 2 },
  { pattern: /\b(three|3)\s*(people|persons|guests)?\b|for\s*three\b/i, size: 3 },
  { pattern: /\b(four|4)\s*(people|persons|guests)?\b|for\s*four\b/i, size: 4 },
  { pattern: /\b(five|5)\s*(people|persons|guests)?\b|for\s*five\b/i, size: 5 },
  { pattern: /\b(six|6)\s*(people|persons|guests)?\b|for\s*six\b/i, size: 6 },
];

const CONFIRM_PATTERNS = [
  { pattern: /\b(yes|yeah|yep|sure|confirm|perfect|great|sounds\s+good|book\s+it|please|absolutely|do\s+it|correct|right)\b/i, confirmed: true },
  { pattern: /\b(no|nope|change|different|wait|wrong|not\s+right)\b/i, confirmed: false },
];

function parseTime(transcript: string): string | null {
  const lower = transcript.toLowerCase();
  for (const { pattern, parse } of TIME_PATTERNS) {
    const match = lower.match(pattern);
    if (match) {
      return parse(match);
    }
  }
  return null;
}

function parsePartySize(transcript: string): number | null {
  const lower = transcript.toLowerCase();
  for (const { pattern, size } of PARTY_SIZE_PATTERNS) {
    if (pattern.test(lower)) {
      return size;
    }
  }
  return null;
}

function parseConfirmation(transcript: string): boolean | null {
  const lower = transcript.toLowerCase();
  for (const { pattern, confirmed } of CONFIRM_PATTERNS) {
    if (pattern.test(lower)) {
      return confirmed;
    }
  }
  return null;
}

// Date parsing patterns
function parseDate(transcript: string): string | null {
  const lower = transcript.toLowerCase();
  const today = new Date();

  // "today"
  if (/\btoday\b/i.test(lower)) {
    return formatDateYMD(today);
  }

  // "tomorrow"
  if (/\btomorrow\b/i.test(lower)) {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateYMD(tomorrow);
  }

  // "day after tomorrow"
  if (/\bday\s+after\s+tomorrow\b/i.test(lower)) {
    const dayAfter = new Date(today);
    dayAfter.setDate(dayAfter.getDate() + 2);
    return formatDateYMD(dayAfter);
  }

  // Specific date: "the 25th", "on the 25th", "for the 25th"
  const ordinalMatch = lower.match(/\b(?:the\s+|on\s+(?:the\s+)?|for\s+(?:the\s+)?)?(\d{1,2})(?:st|nd|rd|th)\b/i);
  if (ordinalMatch) {
    const dayOfMonth = parseInt(ordinalMatch[1]!);
    if (dayOfMonth >= 1 && dayOfMonth <= 31) {
      const targetDate = new Date(today);
      targetDate.setDate(dayOfMonth);
      // If the date has passed this month, assume next month
      if (targetDate < today) {
        targetDate.setMonth(targetDate.getMonth() + 1);
      }
      return formatDateYMD(targetDate);
    }
  }

  // Month and day: "January 25th", "Jan 25", "25th of January"
  const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
                      'july', 'august', 'september', 'october', 'november', 'december'];
  const monthAbbrevs = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  for (let m = 0; m < 12; m++) {
    const monthPattern = `(?:${monthNames[m]}|${monthAbbrevs[m]})`;
    // "January 25th" or "January 25"
    const pattern1 = new RegExp(`\\b${monthPattern}\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, 'i');
    // "25th of January" or "25 January"
    const pattern2 = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?${monthPattern}\\b`, 'i');

    const match1 = lower.match(pattern1);
    const match2 = lower.match(pattern2);
    const dayStr = match1?.[1] || match2?.[1];

    if (dayStr) {
      const dayOfMonth = parseInt(dayStr);
      if (dayOfMonth >= 1 && dayOfMonth <= 31) {
        const targetDate = new Date(today.getFullYear(), m, dayOfMonth);
        // If the date has passed this year, assume next year
        if (targetDate < today) {
          targetDate.setFullYear(targetDate.getFullYear() + 1);
        }
        return formatDateYMD(targetDate);
      }
    }
  }

  // Day names: "monday", "tuesday", etc.
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  for (let i = 0; i < dayNames.length; i++) {
    const pattern = new RegExp(`\\b(this\\s+)?${dayNames[i]}\\b|\\bnext\\s+${dayNames[i]}\\b`, 'i');
    if (pattern.test(lower)) {
      const targetDay = i;
      const currentDay = today.getDay();
      let daysUntil = targetDay - currentDay;
      if (daysUntil <= 0) daysUntil += 7; // Next occurrence
      if (/\bnext\b/i.test(lower)) daysUntil += 7; // "next monday" = following week
      const targetDate = new Date(today);
      targetDate.setDate(targetDate.getDate() + daysUntil);
      return formatDateYMD(targetDate);
    }
  }

  // "in X days"
  const inDaysMatch = lower.match(/\bin\s+(\d+|two|three|four|five|six|seven)\s+days?\b/i);
  if (inDaysMatch) {
    const numMap: Record<string, number> = { two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7 };
    const days = numMap[inDaysMatch[1]!.toLowerCase()] ?? parseInt(inDaysMatch[1]!);
    const targetDate = new Date(today);
    targetDate.setDate(targetDate.getDate() + days);
    return formatDateYMD(targetDate);
  }

  return null;
}

function formatDateYMD(date: Date): string {
  return date.toISOString().split('T')[0]!;
}

function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (dateStr === formatDateYMD(today)) return 'today';
  if (dateStr === formatDateYMD(tomorrow)) return 'tomorrow';

  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

// =============================================================================
// HELPERS
// =============================================================================

function formatTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const h = hours! % 12 || 12;
  const ampm = hours! >= 12 ? 'PM' : 'AM';
  return minutes ? `${h}:${minutes.toString().padStart(2, '0')} ${ampm}` : `${h} ${ampm}`;
}

function getTimeSlots(): string[] {
  const hour = new Date().getHours();
  if (hour < 15) {
    return [...LUNCH_TIME_SLOTS, ...DINNER_TIME_SLOTS];
  }
  return DINNER_TIME_SLOTS;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function RestaurantDetailPage({
  isActive,
  onBack,
  onBookingComplete,
  onVoiceHintsChange,
  initialSlots,
  stayCheckIn,
  stayCheckOut,
}: RestaurantDetailPageProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [focusedDateIndex, setFocusedDateIndex] = useState(0);
  const [focusedTimeIndex, setFocusedTimeIndex] = useState(0);
  const [focusedSizeIndex, setFocusedSizeIndex] = useState(1); // Default to 2 people
  const [bookingStep, setBookingStep] = useState<BookingStep>('select-date');
  const [booking, setBooking] = useState<RestaurantBooking>({
    time: null,
    partySize: null,
    date: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmFocusedButton, setConfirmFocusedButton] = useState<'confirm' | 'change'>('confirm');
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate available dates from stay dates
  const availableDates = useMemo(() => {
    const dates: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = stayCheckIn ? new Date(stayCheckIn) : today;
    const end = stayCheckOut ? new Date(stayCheckOut) : new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    // Start from today or check-in, whichever is later
    const effectiveStart = start > today ? start : today;

    const current = new Date(effectiveStart);
    while (current < end) {
      dates.push(formatDateYMD(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }, [stayCheckIn, stayCheckOut]);

  // Voice recognition state
  const [voiceState, setVoiceState] = useState<'idle' | 'listening' | 'processing'>('idle');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceFeedback, setVoiceFeedback] = useState<string | null>(null);
  const recognitionRef = useRef<ReturnType<typeof createSpeechRecognition>>(null);
  const transcriptRef = useRef('');

  // Refs for voice processing
  const bookingRef = useRef(booking);
  const bookingStepRef = useRef(bookingStep);
  const initialSlotsProcessedRef = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    bookingRef.current = booking;
    bookingStepRef.current = bookingStep;
  }, [booking, bookingStep]);

  // ==========================================================================
  // INITIAL SLOTS HANDLING
  // ==========================================================================

  useEffect(() => {
    // Prevent double-execution in StrictMode
    if (initialSlotsProcessedRef.current) return;
    initialSlotsProcessedRef.current = true;

    if (initialSlots) {
      const newBooking: RestaurantBooking = {
        time: null,
        partySize: null,
        date: null,
      };

      // Parse time from initial slots or transcript
      if (initialSlots.time) {
        newBooking.time = initialSlots.time;
      } else if (initialSlots.transcript) {
        const parsedTime = parseTime(initialSlots.transcript);
        if (parsedTime) newBooking.time = parsedTime;
      }

      // Parse party size from initial slots or transcript
      if (initialSlots.partySize) {
        newBooking.partySize = initialSlots.partySize;
      } else if (initialSlots.transcript) {
        const parsedSize = parsePartySize(initialSlots.transcript);
        if (parsedSize) newBooking.partySize = parsedSize;
      }

      // Parse date from transcript (e.g., "tomorrow", "Monday", etc.)
      if (initialSlots.transcript) {
        const parsedDate = parseDate(initialSlots.transcript);
        if (parsedDate) newBooking.date = parsedDate;
      }

      setBooking(newBooking);

      // Determine starting step based on what slots we have
      if (newBooking.date && newBooking.time && newBooking.partySize) {
        // All slots filled - go to review
        setBookingStep('review');
        setTimeout(() => {
          speak(`A table for ${newBooking.partySize} at ${RESTAURANT_INFO.name} at ${formatTime(newBooking.time!)} ${formatDateDisplay(newBooking.date!)}. Is this correct?`);
        }, 500);
      } else if (newBooking.date && newBooking.time) {
        // Have date and time, need party size
        setBookingStep('select-party-size');
        setTimeout(() => {
          speak(`${formatTime(newBooking.time!)} ${formatDateDisplay(newBooking.date!)}, excellent choice. How many guests will be dining?`);
        }, 500);
      } else if (newBooking.date && newBooking.partySize) {
        // Have date and party size, need time
        setBookingStep('select-time');
        setTimeout(() => {
          speak(`A table for ${newBooking.partySize} ${formatDateDisplay(newBooking.date!)}. What time would you like to dine?`);
        }, 500);
      } else if (newBooking.date) {
        // Have only date, need time
        setBookingStep('select-time');
        setTimeout(() => {
          speak(`${formatDateDisplay(newBooking.date!)}. What time would you like to book a table?`);
        }, 500);
      } else if (newBooking.time) {
        // Have time but no date - ask for date first
        setBookingStep('select-date');
        setTimeout(() => {
          speak(`A table at ${formatTime(newBooking.time!)}. Which day would you like?`);
        }, 500);
      } else {
        // No slots - ask for day
        setBookingStep('select-date');
        setTimeout(() => {
          speak(`Welcome to ${RESTAURANT_INFO.name}. Which day would you like to book a table?`);
        }, 500);
      }
    } else {
      // No initial slots - welcome message, start at date selection
      setBookingStep('select-date');
      setTimeout(() => {
        speak(`Welcome to ${RESTAURANT_INFO.name}. Which day would you like to dine?`);
      }, 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================================================
  // VOICE HINTS
  // ==========================================================================

  useEffect(() => {
    if (!isActive) return;

    let hints: VoiceHintConfig;
    switch (bookingStep) {
      case 'select-date':
        hints = {
          title: 'Choose a day',
          hints: ['Today', 'Tomorrow', 'The 25th'],
          variant: 'default',
        };
        break;
      case 'select-time':
        hints = {
          title: 'Say a time',
          hints: ['8 PM', '7:30 tonight', 'Half past eight'],
          variant: 'default',
        };
        break;
      case 'select-party-size':
        hints = {
          title: 'How many guests?',
          hints: ['Two people', 'Just me', 'Four guests'],
          variant: 'default',
        };
        break;
      case 'review':
      case 'confirm':
        hints = {
          title: 'Confirm booking',
          hints: ['Yes, book it', 'Change the time', 'Cancel'],
          variant: 'confirm',
        };
        break;
    }
    onVoiceHintsChange?.(hints);

    return () => onVoiceHintsChange?.(null);
  }, [isActive, bookingStep, onVoiceHintsChange]);

  // ==========================================================================
  // IMAGE SLIDESHOW
  // ==========================================================================

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % RESTAURANT_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // ==========================================================================
  // SPEAK HELPER
  // ==========================================================================

  const speak = useCallback((text: string, onComplete?: () => void) => {
    setVoiceFeedback(text);
    zaxiaVoice.speak(text, {
      onEnd: () => {
        setVoiceFeedback(null);
        onComplete?.();
      },
    });
  }, []);

  // ==========================================================================
  // VOICE ACTION HANDLER
  // ==========================================================================

  // Cancel/back detection patterns
  const isCancelCommand = (transcript: string): boolean => {
    return /\b(cancel|nevermind|never\s*mind|forget\s*it|stop|quit|exit|close)\b/i.test(transcript);
  };

  const isBackCommand = (transcript: string): boolean => {
    return /\b(go\s*back|back|previous|change\s*that)\b/i.test(transcript);
  };

  const handleVoiceAction = useCallback((action: VoiceAction, rawTranscript: string) => {
    const currentBooking = bookingRef.current;
    const currentStep = bookingStepRef.current;

    // Check for cancel/back commands directly from transcript
    const wantsCancel = isCancelCommand(rawTranscript) || action.type === 'CANCEL' || action.type === 'CLOSE';
    const wantsBack = isBackCommand(rawTranscript) || action.type === 'BACK';

    if (wantsCancel) {
      speak('Booking cancelled. Goodbye!', () => onBack());
      return;
    }

    if (wantsBack) {
      if (currentStep === 'select-date') {
        speak('Goodbye!', () => onBack());
      } else if (currentStep === 'select-time') {
        setBookingStep('select-date');
        speak('Which day would you like?');
      } else if (currentStep === 'select-party-size') {
        setBookingStep('select-time');
        speak('What time would you like?');
      } else if (currentStep === 'review' || currentStep === 'confirm') {
        setBookingStep('select-party-size');
        speak('How many guests will be dining?');
      }
      return;
    }

    // Step-specific handling
    switch (currentStep) {
      case 'select-date': {
        // Try to parse date from transcript
        const date = parseDate(rawTranscript);
        if (date) {
          setBooking(prev => ({ ...prev, date }));
          setBookingStep('select-time');
          speak(`${formatDateDisplay(date)}. What time would you like to dine?`);
        } else {
          speak("Which day would you like? You can say today, tomorrow, or a specific day like Monday or the 25th.");
        }
        break;
      }

      case 'select-time': {
        // Try to parse time from transcript
        const time = parseTime(rawTranscript);
        if (time) {
          setBooking(prev => ({ ...prev, time }));
          if (currentBooking.partySize) {
            setBookingStep('review');
            speak(`A table for ${currentBooking.partySize} at ${formatTime(time)} ${formatDateDisplay(currentBooking.date!)}. Is this correct?`);
          } else {
            setBookingStep('select-party-size');
            speak(`${formatTime(time)}, excellent choice. How many guests will be dining?`);
          }
        } else {
          speak("What time works best for you? You can say something like 8 PM or 7:30 tonight.");
        }
        break;
      }

      case 'select-party-size': {
        // Try to parse party size
        const size = parsePartySize(rawTranscript);
        if (size) {
          setBooking(prev => ({ ...prev, partySize: size }));
          setBookingStep('review');
          speak(`A table for ${size} at ${RESTAURANT_INFO.name} at ${formatTime(currentBooking.time!)} ${formatDateDisplay(currentBooking.date!)}. Is this correct?`);
        } else {
          speak("How many people will be joining you?");
        }
        break;
      }

      case 'review': {
        const confirmed = parseConfirmation(rawTranscript);
        const time = parseTime(rawTranscript);
        const size = parsePartySize(rawTranscript);
        const date = parseDate(rawTranscript);
        const wantsChangeTime = /\b(change|different)\s*(the\s*)?(time|hour)\b/i.test(rawTranscript);
        const wantsChangeGuests = /\b(change|different)\s*(the\s*)?(guest|people|party|number)\b/i.test(rawTranscript);
        const wantsChangeDate = /\b(change|different)\s*(the\s*)?(day|date)\b/i.test(rawTranscript);

        // Helper to build the confirmation message
        const buildConfirmMsg = (t: string, s: number, d: string) =>
          `A table for ${s} at ${RESTAURANT_INFO.name} at ${formatTime(t)} ${formatDateDisplay(d)}. Is this correct?`;

        // User explicitly wants to change time
        if (wantsChangeTime) {
          setBookingStep('select-time');
          speak("What time would you prefer?");
          break;
        }

        // User explicitly wants to change party size
        if (wantsChangeGuests) {
          setBookingStep('select-party-size');
          speak("How many guests will be dining?");
          break;
        }

        // User explicitly wants to change date
        if (wantsChangeDate) {
          setBookingStep('select-date');
          speak("Which day would you prefer?");
          break;
        }

        // User provided a new date directly
        if (date) {
          setBooking(prev => ({ ...prev, date }));
          speak(`Updated to ${formatDateDisplay(date)}. ${buildConfirmMsg(currentBooking.time!, currentBooking.partySize!, date)}`);
          break;
        }

        // User provided a new time directly
        if (time) {
          setBooking(prev => ({ ...prev, time }));
          speak(`Updated to ${formatTime(time)}. ${buildConfirmMsg(time, currentBooking.partySize!, currentBooking.date!)}`);
          break;
        }

        // User provided a new party size directly
        if (size) {
          setBooking(prev => ({ ...prev, partySize: size }));
          speak(`Updated to ${size} guests. ${buildConfirmMsg(currentBooking.time!, size, currentBooking.date!)}`);
          break;
        }

        if (confirmed === true) {
          setBookingStep('confirm');
          speak("Should I book it?");
        } else if (confirmed === false) {
          speak("What would you like to change? Say 'change time', 'change day', or 'change guests', or just tell me the new value.");
        } else {
          speak("Is this correct? Say yes to confirm, or tell me what you'd like to change.");
        }
        break;
      }

      case 'confirm': {
        const confirmed = parseConfirmation(rawTranscript);
        if (confirmed === true) {
          // Create booking
          setIsSubmitting(true);
          const { time, partySize, date } = currentBooking;

          if (time && partySize && date) {
            const [hours, minutes] = time.split(':').map(Number);
            const datetime = new Date(date);
            datetime.setHours(hours!, minutes!, 0, 0);

            const bookingRequest: CreateBookingRequest = {
              serviceId: 'restaurant-terra',
              serviceName: `Table for ${partySize} at ${RESTAURANT_INFO.name}`,
              category: 'RESTAURANT',
              datetime: datetime.toISOString(),
              durationMinutes: 90,
              price: null,
              location: RESTAURANT_INFO.name,
            };

            onBookingComplete(bookingRequest)
              .then(() => {
                setIsSubmitting(false);
                setShowConfirmation(true);
                speak(`Booking confirmed! Your table for ${partySize} at ${RESTAURANT_INFO.name} is set for ${formatTime(time)} ${formatDateDisplay(date)}. I've sent the details to your room.`, () => {
                  setTimeout(() => onBack(), 2000);
                });
              })
              .catch(() => {
                setIsSubmitting(false);
                speak("I'm sorry, there was an issue confirming your booking. Please try again.");
              });
          }
        } else if (confirmed === false) {
          setBookingStep('review');
          speak("No problem. What would you like to change - the time or the number of guests?");
        } else {
          speak("Should I book it? Say yes to confirm.");
        }
        break;
      }
    }
  }, [onBack, onBookingComplete, speak]);

  // ==========================================================================
  // VOICE RECOGNITION
  // ==========================================================================

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setVoiceState('idle');
    setVoiceTranscript('');
  }, []);

  const processTranscript = useCallback(() => {
    const transcript = transcriptRef.current.trim();
    if (!transcript) {
      setVoiceState('idle');
      return;
    }

    setVoiceState('processing');

    // Use the voice engine
    const context = createVoiceContext('overlay', {});
    const action = processVoiceInput(transcript, context);

    handleVoiceAction(action, transcript);
    setVoiceState('idle');
    setVoiceTranscript('');
  }, [handleVoiceAction]);

  const startListening = useCallback(() => {
    if (!isSpeechRecognitionAvailable()) {
      setVoiceFeedback('Speech recognition not available');
      return;
    }

    // Cancel any ongoing speech
    zaxiaVoice.cancel();

    // Cancel any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const recognition = createSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;
    setVoiceState('listening');
    setVoiceTranscript('');
    transcriptRef.current = '';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.[0]) {
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setVoiceTranscript(currentTranscript);
      transcriptRef.current = currentTranscript;
    };

    recognition.onerror = () => {
      setVoiceState('idle');
    };

    recognition.onend = () => {
      // Recognition ended naturally
    };

    recognition.start();
  }, []);

  // Reset confirm button focus each time the user reaches review/confirm step
  useEffect(() => {
    if (bookingStep === 'review' || bookingStep === 'confirm') setConfirmFocusedButton('confirm');
  }, [bookingStep]);

  // ==========================================================================
  // KEYBOARD NAVIGATION
  // ==========================================================================

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      // Spacebar = push-to-talk
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        if (voiceState === 'idle') {
          startListening();
        }
        return;
      }

      // Escape = back
      if (e.key === 'Escape') {
        e.preventDefault();
        if (bookingStep === 'select-date') {
          onBack();
        } else if (bookingStep === 'select-time') {
          setBookingStep('select-date');
        } else if (bookingStep === 'select-party-size') {
          setBookingStep('select-time');
        } else if (bookingStep === 'review' || bookingStep === 'confirm') {
          setBookingStep('select-party-size');
        }
        return;
      }

      const timeSlots = getTimeSlots();

      // Arrow navigation for date/time/size selection
      if (bookingStep === 'select-date') {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          setFocusedDateIndex(prev => (prev + 1) % availableDates.length);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setFocusedDateIndex(prev => (prev - 1 + availableDates.length) % availableDates.length);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          const selectedDate = availableDates[focusedDateIndex];
          if (selectedDate) {
            setBooking(prev => ({ ...prev, date: selectedDate }));
            setBookingStep('select-time');
            speak(`${formatDateDisplay(selectedDate)}. What time would you like to dine?`);
          }
        }
      } else if (bookingStep === 'select-time') {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          setFocusedTimeIndex(prev => (prev + 1) % timeSlots.length);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setFocusedTimeIndex(prev => (prev - 1 + timeSlots.length) % timeSlots.length);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          const selectedTime = timeSlots[focusedTimeIndex];
          if (selectedTime) {
            setBooking(prev => ({ ...prev, time: selectedTime }));
            setBookingStep('select-party-size');
            speak(`${formatTime(selectedTime)}, excellent choice. How many guests will be dining?`);
          }
        }
      } else if (bookingStep === 'select-party-size') {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          setFocusedSizeIndex(prev => (prev + 1) % PARTY_SIZES.length);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setFocusedSizeIndex(prev => (prev - 1 + PARTY_SIZES.length) % PARTY_SIZES.length);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          const selectedSize = PARTY_SIZES[focusedSizeIndex];
          if (selectedSize !== undefined) {
            setBooking(prev => ({ ...prev, partySize: selectedSize }));
            setBookingStep('review');
            speak(`A table for ${selectedSize} at ${RESTAURANT_INFO.name} at ${formatTime(booking.time!)} ${formatDateDisplay(booking.date!)}. Is this correct?`);
          }
        }
      } else if (bookingStep === 'review' || bookingStep === 'confirm') {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setConfirmFocusedButton('confirm');
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          setConfirmFocusedButton('change');
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (confirmFocusedButton === 'confirm') {
            handleVoiceAction({ type: 'CONFIRM' }, 'yes');
          } else {
            setBookingStep('select-time');
            speak('What time would you like?');
          }
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (voiceState === 'listening') {
          stopListening();
          processTranscript();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isActive, voiceState, bookingStep, focusedDateIndex, focusedTimeIndex, focusedSizeIndex, availableDates, booking.time, confirmFocusedButton, handleVoiceAction, startListening, stopListening, processTranscript, speak, onBack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
      zaxiaVoice.cancel();
    };
  }, [stopListening]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  const timeSlots = getTimeSlots();

  return (
    <div
      ref={containerRef}
      className="restaurant-detail h-full flex flex-col bg-gradient-to-b from-[#0D1B2A] to-[#1B263B] text-white"
    >
      {/* Header */}
      <div className="restaurant-header flex items-center justify-between px-6 py-4 border-b border-white/10">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="text-center">
          <h1 className="text-xl font-light tracking-wide">{RESTAURANT_INFO.name}</h1>
          <p className="text-sm text-amber-400/80">{RESTAURANT_INFO.cuisine}</p>
        </div>
        <div className="w-20" /> {/* Spacer for centering */}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Image */}
        <div className="w-1/2 relative overflow-hidden">
          <AnimatePresence mode="wait">
            <img
              key={currentImageIndex}
              src={RESTAURANT_IMAGES[currentImageIndex]}
              alt={RESTAURANT_INFO.name}
              className="absolute inset-0 w-full h-full object-cover slide-img-fade"
            />
          </AnimatePresence>
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0D1B2A]/80" />

          {/* Restaurant info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white/80 text-sm leading-relaxed">{RESTAURANT_INFO.description}</p>
            <div className="mt-3 flex items-center gap-4 text-white/60 text-xs">
              <span>📍 {RESTAURANT_INFO.location}</span>
            </div>
          </div>
        </div>

        {/* Right: Booking Flow */}
        <div className="restaurant-booking-panel w-1/2 p-8 flex flex-col">
          {/* Step indicator - 4 steps: Date, Time, Party Size, Confirm */}
          <div className="flex items-center gap-2 mb-8">
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
              bookingStep === 'select-date' ? 'bg-amber-500 text-white' : booking.date ? 'bg-green-500 text-white' : 'bg-white/20 text-white/50'
            )}>
              {booking.date ? <Check size={16} /> : '1'}
            </div>
            <div className={clsx('flex-1 h-0.5', booking.date ? 'bg-green-500' : 'bg-white/20')} />
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
              bookingStep === 'select-time' ? 'bg-amber-500 text-white' : booking.time ? 'bg-green-500 text-white' : 'bg-white/20 text-white/50'
            )}>
              {booking.time ? <Check size={16} /> : '2'}
            </div>
            <div className={clsx('flex-1 h-0.5', booking.time ? 'bg-green-500' : 'bg-white/20')} />
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
              bookingStep === 'select-party-size' ? 'bg-amber-500 text-white' : booking.partySize ? 'bg-green-500 text-white' : 'bg-white/20 text-white/50'
            )}>
              {booking.partySize ? <Check size={16} /> : '3'}
            </div>
            <div className={clsx('flex-1 h-0.5', booking.partySize ? 'bg-green-500' : 'bg-white/20')} />
            <div className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
              bookingStep === 'review' || bookingStep === 'confirm' ? 'bg-amber-500 text-white' : 'bg-white/20 text-white/50'
            )}>
              4
            </div>
          </div>

          {/* Booking content */}
          <AnimatePresence mode="wait">
            {/* Step 0: Select Date */}
            {bookingStep === 'select-date' && (
              <motion.div
                key="date"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1"
              >
                <h2 className="text-2xl font-light mb-2 flex items-center gap-3">
                  <Calendar className="text-amber-400" size={24} />
                  Select a Day
                </h2>
                <p className="text-white/60 mb-6">Say a day or use arrow keys to select</p>

                <div className="grid grid-cols-4 gap-3">
                  {availableDates.map((date, index) => (
                    <button
                      key={date}
                      onClick={() => {
                        setBooking(prev => ({ ...prev, date }));
                        setBookingStep('select-time');
                        speak(`${formatDateDisplay(date)}. What time would you like to dine?`);
                      }}
                      className={clsx(
                        'py-3 px-4 rounded-xl text-center transition-all',
                        focusedDateIndex === index
                          ? 'bg-amber-500 text-white ring-2 ring-amber-400 ring-offset-2 ring-offset-[#0D1B2A]'
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      )}
                    >
                      {formatDateDisplay(date)}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 1: Select Time */}
            {bookingStep === 'select-time' && (
              <motion.div
                key="time"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1"
              >
                <h2 className="text-2xl font-light mb-2 flex items-center gap-3">
                  <Clock className="text-amber-400" size={24} />
                  Select a Time
                </h2>
                <p className="text-white/60 mb-6">Say a time or select from available slots</p>

                <div className="grid grid-cols-4 gap-3">
                  {timeSlots.map((time, index) => (
                    <button
                      key={time}
                      onClick={() => {
                        setBooking(prev => ({ ...prev, time }));
                        setBookingStep('select-party-size');
                        speak(`${formatTime(time)}, excellent choice. How many guests will be dining?`);
                      }}
                      className={clsx(
                        'py-3 px-4 rounded-xl text-center transition-all',
                        focusedTimeIndex === index
                          ? 'bg-amber-500 text-white ring-2 ring-amber-400 ring-offset-2 ring-offset-[#0D1B2A]'
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      )}
                    >
                      {formatTime(time)}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: Select Party Size */}
            {bookingStep === 'select-party-size' && (
              <motion.div
                key="size"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1"
              >
                <h2 className="text-2xl font-light mb-2 flex items-center gap-3">
                  <Users className="text-amber-400" size={24} />
                  Number of Guests
                </h2>
                <p className="text-white/60 mb-6">
                  Dining at {formatTime(booking.time!)}
                </p>

                <div className="grid grid-cols-3 gap-4">
                  {PARTY_SIZES.map((size, index) => (
                    <button
                      key={size}
                      onClick={() => {
                        setBooking(prev => ({ ...prev, partySize: size }));
                        setBookingStep('review');
                        speak(`A table for ${size} at ${RESTAURANT_INFO.name} at ${formatTime(booking.time!)} ${formatDateDisplay(booking.date!)}. Is this correct?`);
                      }}
                      className={clsx(
                        'py-6 px-4 rounded-xl text-center transition-all',
                        focusedSizeIndex === index
                          ? 'bg-amber-500 text-white ring-2 ring-amber-400 ring-offset-2 ring-offset-[#0D1B2A]'
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      )}
                    >
                      <div className="text-3xl mb-2">{size}</div>
                      <div className="text-sm text-white/60">
                        {size === 1 ? 'Guest' : 'Guests'}
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 3: Review & Confirm */}
            {(bookingStep === 'review' || bookingStep === 'confirm') && (
              <motion.div
                key="review"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1"
              >
                <h2 className="text-2xl font-light mb-6 flex items-center gap-3">
                  <Calendar className="text-amber-400" size={24} />
                  Confirm Your Reservation
                </h2>

                <div className="bg-white/5 rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                      🍷
                    </div>
                    <div>
                      <h3 className="text-lg font-medium">{RESTAURANT_INFO.name}</h3>
                      <p className="text-sm text-white/60">{RESTAURANT_INFO.cuisine}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-3 text-white/80">
                      <Clock size={18} className="text-amber-400" />
                      <span>{formatTime(booking.time!)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/80">
                      <Users size={18} className="text-amber-400" />
                      <span>{booking.partySize} {booking.partySize === 1 ? 'Guest' : 'Guests'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/80">
                      <Calendar size={18} className="text-amber-400" />
                      <span className="capitalize">{formatDateDisplay(booking.date!)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setBookingStep('select-time');
                      speak('What time would you like?');
                    }}
                    className={clsx(
                      'flex-1 py-3 px-6 rounded-xl transition-colors',
                      confirmFocusedButton === 'change'
                        ? 'bg-white/20 text-white ring-2 ring-white/50'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    )}
                  >
                    Change
                  </button>
                  <button
                    onClick={() => {
                      // Trigger confirmation
                      handleVoiceAction({ type: 'CONFIRM' }, 'yes');
                    }}
                    disabled={isSubmitting}
                    className={clsx(
                      'flex-1 py-3 px-6 rounded-xl font-medium transition-all',
                      isSubmitting
                        ? 'bg-amber-500/50 text-white/50 cursor-not-allowed'
                        : confirmFocusedButton === 'confirm'
                        ? 'bg-amber-600 text-white ring-2 ring-amber-400'
                        : 'bg-amber-500 text-white hover:bg-amber-600'
                    )}
                  >
                    {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Voice feedback */}
          <AnimatePresence>
            {(voiceFeedback || voiceState === 'listening') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="mt-auto pt-6 border-t border-white/10"
              >
                <div className="flex items-center gap-4">
                  <div className={clsx(
                    'w-12 h-12 rounded-full flex items-center justify-center',
                    voiceState === 'listening' ? 'bg-red-500 animate-pulse' : 'bg-amber-500/20'
                  )}>
                    <Mic size={20} className={voiceState === 'listening' ? 'text-white' : 'text-amber-400'} />
                  </div>
                  <div className="flex-1">
                    {voiceState === 'listening' && (
                      <p className="text-white/80">
                        {voiceTranscript || 'Listening...'}
                      </p>
                    )}
                    {voiceFeedback && voiceState !== 'listening' && (
                      <p className="text-white/80 italic">&ldquo;{voiceFeedback}&rdquo;</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Confirmation overlay */}
      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center"
            >
              <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-6">
                <Check size={40} className="text-white" />
              </div>
              <h2 className="text-3xl font-light mb-2">Booking Confirmed!</h2>
              <p className="text-white/60">
                Table for {booking.partySize} at {formatTime(booking.time!)} {formatDateDisplay(booking.date!)}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default RestaurantDetailPage;
